import { ListItem, Typography, Box, useTheme, Tooltip } from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { TasksResponse } from "@/modules/tasks/dtos";
import {
  checkIfTaskIsSubmittedOverdue,
  formatSmartDate,
  capitalizeFirstLetter,
  checkIfTaskIsOverdue,
} from "@/modules/tasks/utils";
import { TaskStatusesBackend } from "@/modules/tasks/enums";
import TaskCompletionCheckbox from "@/modules/tasks/components/task-completion-checkbox";
interface MeetingItemProps {
  meeting: TasksResponse;
  onCheckboxChange?: (meeting: TasksResponse) => void;
  isLoading?: boolean;
  onClick?: () => void;
}

const MeetingItem = ({
  meeting,
  onCheckboxChange,
  isLoading = false,
  onClick,
}: MeetingItemProps) => {
  const theme = useTheme();
  const isCompleted = meeting?.status === TaskStatusesBackend.completed;

  const handleToggleComplete = () => {
    onCheckboxChange?.(meeting);
  };

  // Get the participant name or fall back to meeting title
  const displayTitle = meeting.Participants?.[0]?.User?.name || meeting?.title;

  return (
    <ListItem
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.xs,
        padding: "12px 16px",
        borderBottom: `1px solid ${nawyTheme.nawy.colors.semantic.border.secondary}`,
        width: "100%",
      }}
    >
      {/* Row 1: Icon Button + Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.xs,
          width: "100%",
        }}
      >
        <Tooltip
          title={
            checkIfTaskIsOverdue(meeting.status, meeting.dueDate)
              ? "This task is past due, so it can't be marked as done"
              : ""
          }
          disableInteractive={
            !checkIfTaskIsOverdue(meeting.status, meeting.dueDate)
          }
          disableFocusListener={
            !checkIfTaskIsOverdue(meeting.status, meeting.dueDate)
          }
          disableTouchListener={
            !checkIfTaskIsOverdue(meeting.status, meeting.dueDate)
          }
          arrow
        >
          <span>
            <TaskCompletionCheckbox
              checked={isCompleted}
              onChange={handleToggleComplete}
              disabled={
                isCompleted ||
                checkIfTaskIsOverdue(meeting.status, meeting.dueDate)
              }
              size={20}
              isLoading={isLoading}
            />
          </span>
        </Tooltip>

        {/* Title */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.xs,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <Tooltip
            title={capitalizeFirstLetter(displayTitle)}
            placement="top"
            arrow
          >
            <Typography
              sx={{
                color: isCompleted
                  ? theme.nawy.colors.semantic.text.tertiary
                  : theme.nawy.colors.semantic.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                fontFamily: nawyTheme.nawy.typography.english.fontFamily,
              }}
            >
              {capitalizeFirstLetter(displayTitle)}
            </Typography>
          </Tooltip>

          {/* Row 2: Due Date */}
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
              color: checkIfTaskIsSubmittedOverdue(
                meeting?.status,
                meeting?.dueDate,
                meeting?.completedAt || ""
              )
                ? nawyTheme.nawy.colors.semantic.text.extension.negative
                : theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            {formatSmartDate(meeting.dueDate)}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
};

export default MeetingItem;
